/**
 * Order presentation helpers: status wording, money, and Prohandel stock state.
 *
 * Shared by the orders list and the order detail page so the two cannot show
 * different words for the same row.
 */

/**
 * Plain-language label for a stored `fulfillment_status`.
 *
 * The stored values are unchanged (no migration); this is the display layer.
 *
 * `needs_branch` is the awkward one: the backend writes it from TWO unrelated
 * situations - the pick strategy being `manual` (nobody has chosen a branch yet)
 * and a branch being assigned but no store able to take it. They mean opposite
 * things, so the label is resolved with the branch number: no branch means the
 * operator still has to pick one, a branch present means it is waiting on a shop.
 */
export function statusLabel(status: string, branchNumber: number | null): string {
	switch (status) {
		case "ready":
			return "Ready to ship";
		case "no_single_branch":
			// The oversell case: Farfetch sold something we do not have. "no single
			// branch" described the branch-selection mechanism rather than the fact
			// an operator needs.
			//
			// Deliberately NOT split on whether a branch is recorded. It was, and
			// that was wrong: stock is 0 either way (verified across all 37 such
			// orders on prod), so a branch number only reflects an earlier
			// assignment attempt. "Out of stock at branch" implied the item might
			// exist at some OTHER branch, which is not the case.
			return "No stock available";
		case "partial_stock":
			return "Partly in stock";
		case "needs_branch":
			return branchNumber === null ? "Pick a branch" : "Waiting for shop";
		case "pending":
			// Farfetch's payment/fraud screening, not ours: they run it and tell us
			// via ShippingOrders.FraudApproved. "Awaiting fraud check" read as if
			// Burresi were checking something, so the label names who is doing it.
			return "Awaiting Farfetch approval";
		case "cancelled":
			return "Cancelled";
		case "fulfilled":
			return "Fulfilled";
		default:
			return status;
	}
}

/** Badge tone: red where an operator must act, neutral where we are waiting. */
export function statusTone(status: string): "default" | "secondary" | "destructive" | "outline" {
	switch (status) {
		case "ready":
		case "fulfilled":
			return "default";
		case "no_single_branch":
		case "partial_stock":
			return "destructive";
		case "needs_branch":
			return "secondary";
		default:
			return "outline";
	}
}

/**
 * Money with exactly two decimals.
 *
 * The API sends NUMERIC as a string (e.g. "1954.0000"), which rendered raw as
 * "1954.0000 EUR". Parsed and formatted here rather than trusting the wire
 * format. Returns "-" for absent values so a missing total never reads as 0.00.
 */
export function formatMoney(amount: string | number | null, currency: string | null): string {
	if (amount === null || amount === undefined || amount === "") return "-";
	const n = typeof amount === "number" ? amount : Number.parseFloat(amount);
	if (!Number.isFinite(n)) return "-";
	const formatted = n.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
	return currency ? `${formatted} ${currency}` : formatted;
}

/** What Prohandel currently holds for an order's committed stock. */
export type ProhandelState = "none" | "reserved" | "requested" | "moved" | "issued" | "stock_short";

/**
 * Label for the Prohandel stock movement, derived from the reservation ledger
 * rather than a second status column that could drift from it.
 *
 * - `none`     no reservation row: nothing committed in the ERP
 * - `reserved` row exists, `moved_at` NULL: we hold it, Prohandel has not caught
 *              up (an ERP outage must not block a shop, so this state is normal)
 * - `moved`    transferred into the Farfetch branch (600)
 * - `issued`   booked out of 600 entirely
 * - `stock_short` the held-stock sweep re-read the branch and found fewer units
 *              than the reservation claims (DEV-812). Outranks every progress
 *              state because the order cannot be picked as it stands. Nothing is
 *              changed automatically - an operator has to decide.
 */
export function prohandelLabel(
	state: ProhandelState,
	fromBranch?: number | null,
	toBranch?: number | null,
): string {
	const route = fromBranch != null && toBranch != null ? `${fromBranch} \u2192 ${toBranch}` : null;
	switch (state) {
		case "stock_short":
			return "Stock missing at branch";
		case "issued":
			return "Booked out";
		case "moved":
			return route ? `Moved ${route}` : "Moved to 600";
		case "requested":
			// The transferInstruction was accepted (HTTP 201) but that only QUEUES
			// the move - Prohandel applies it their side, so the goods may still be
			// on the shop floor. Saying "moved" here would be a lie an operator
			// could act on.
			return route ? `${route} requested` : "Move requested";
		case "reserved":
			// Held in our ledger, no ERP instruction sent yet (or it failed).
			return "Reserved (ERP pending)";
		default:
			return "Not moved";
	}
}

export function prohandelTone(
	state: ProhandelState,
): "default" | "secondary" | "outline" | "destructive" {
	switch (state) {
		case "stock_short":
			return "destructive";
		case "issued":
		case "moved":
			return "default";
		case "requested":
		case "reserved":
			return "secondary";
		default:
			return "outline";
	}
}
